import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { PoPageDynamicSearchLiterals } from "@po-ui/ng-templates";
import {
  PoDialogService,
  PoDisclaimer,
  PoModalAction,
  PoModalComponent,
  PoNotificationService,
  PoPageAction,
  PoTableAction,
  PoTableColumn,
} from "@po-ui/ng-components";
import { Observable, Subscription, forkJoin, of } from "rxjs";

import { Area } from "src/app/interfaces/area";
import { Tribe } from "src/app/interfaces/tribe";
import { USERS_ADMIN_BASE } from "../area-tribe-admin.component";
import { AreaService } from "../services/area.service";
import { TribeService } from "../services/tribe.service";
import { TableHeightFitter } from "../users-admin.utils";

/** Cadastro de tribes e vínculo tribe × áreas. */
@Component({
  selector: "app-tribes",
  templateUrl: "./tribes.component.html",
  styleUrls: ["./tribes.component.css"],
})
export class TribesComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("tribeModal", { static: true }) tribeModal: PoModalComponent;
  @ViewChild("linkAreasModal", { static: true }) linkAreasModal: PoModalComponent;

  loading = false;
  tribes: Array<Tribe> = [];
  areas: Array<Area> = [];
  rows: Array<Tribe> = [];
  q = "";

  readonly searchLiterals: PoPageDynamicSearchLiterals = {
    searchPlaceholder: "Buscar tribe",
    disclaimerGroupTitle: "Filtros ativos",
  };

  readonly pageActions: Array<PoPageAction> = [
    { label: "Nova tribe", icon: "po-icon-plus", action: () => this.openTribeForm() },
    { label: "Atualizar", icon: "po-icon-refresh", action: () => this.load() },
  ];

  readonly columns: Array<PoTableColumn> = [
    { property: "tribe", label: "Tribe" },
    { property: "areasCount", label: "Áreas", width: "14%", type: "cellTemplate" },
    { property: "usersCount", label: "Usuários", width: "14%", type: "cellTemplate" },
  ];

  readonly tableActions: Array<PoTableAction> = [
    { label: "Vincular áreas", icon: "po-icon-link", action: (t: Tribe) => this.openLinkAreas(t) },
    { label: "Renomear", icon: "po-icon-edit", action: (t: Tribe) => this.openTribeForm(t) },
    { label: "Ver áreas", icon: "po-icon-list", action: (t: Tribe) => this.showAreas(t) },
    { label: "Ver usuários", icon: "po-icon-users", action: (t: Tribe) => this.showUsers(t) },
    { label: "Excluir", icon: "po-icon-delete", type: "danger", action: (t: Tribe) => this.confirmRemove(t) },
  ];

  // Formulário
  editingTribe: Tribe | null = null;
  tribeName = "";
  tribeNameError = "";
  tribePrimaryAction: PoModalAction = { label: "Salvar", action: () => this.saveTribe(), disabled: true };
  readonly tribeCancel: PoModalAction = { label: "Cancelar", action: () => this.tribeModal.close() };

  // Vincular áreas
  linkingTribe: Tribe | null = null;
  linkRows: Array<{ id: string; area: string; currentTribe: string; $selected: boolean }> = [];
  linkVisibleRows: Array<{ id: string; area: string; currentTribe: string; $selected: boolean }> = [];
  linkSearch = "";
  linkedAreaIds: Array<string> = [];
  readonly linkColumns: Array<PoTableColumn> = [
    { property: "area", label: "Área" },
    { property: "currentTribe", label: "Tribe atual", width: "30%" },
  ];
  linkSummary = { entering: [] as Array<Area>, leaving: [] as Array<Area> };
  linkPrimaryAction: PoModalAction = { label: "Salvar vínculos", action: () => this.saveLinks(), disabled: true };
  readonly linkCancel: PoModalAction = { label: "Cancelar", action: () => this.linkAreasModal.close() };

  /** A tabela ocupa o que sobra da tela: a página não rola, só as linhas. */
  readonly tableFit: TableHeightFitter;

  private subscriptions = new Subscription();

  constructor(
    private router: Router,
    private tribeService: TribeService,
    private areaService: AreaService,
    private poDialog: PoDialogService,
    private poNotification: PoNotificationService,
    elementRef: ElementRef<HTMLElement>,
    zone: NgZone,
  ) {
    this.tableFit = new TableHeightFitter(elementRef.nativeElement, zone);
  }

  ngOnInit(): void {
    this.load();
  }

  ngAfterViewInit(): void {
    this.tableFit.start();
  }

  ngOnDestroy(): void {
    this.tableFit.stop();
    this.subscriptions.unsubscribe();
  }

  load(): void {
    this.loading = true;
    this.subscriptions.add(
      forkJoin({ tribes: this.tribeService.list(), areas: this.areaService.list() }).subscribe({
        next: ({ tribes, areas }) => {
          this.tribes = tribes;
          this.areas = areas;
          this.rebuild();
        },
        complete: () => (this.loading = false),
        error: () => (this.loading = false),
      }),
    );
  }

  private rebuild(): void {
    const q = this.q.toLowerCase();
    this.rows = this.tribes
      .filter((t) => !q || t.tribe.toLowerCase().includes(q))
      .sort((a, b) => a.tribe.localeCompare(b.tribe));
  }

  // ── Filtro ──────────────────────────────────────────────────────────────────

  onQuickSearch(q: string): void {
    this.q = (q ?? "").trim();
    this.rebuild();
  }

  onChangeDisclaimers(disclaimers: Array<PoDisclaimer>): void {
    this.q = disclaimers.find((d) => d.property === "search")?.value ?? "";
    this.rebuild();
  }

  // ── Navegação ───────────────────────────────────────────────────────────────

  showAreas(tribe: Tribe): void {
    this.router.navigate([`${USERS_ADMIN_BASE}/areas`], { queryParams: { tribe: tribe.id } });
  }

  showUsers(tribe: Tribe): void {
    this.router.navigate([`${USERS_ADMIN_BASE}/users`], { queryParams: { tribe: tribe.id } });
  }

  // ── Cadastro ────────────────────────────────────────────────────────────────

  openTribeForm(tribe: Tribe | null = null): void {
    this.editingTribe = tribe;
    this.tribeName = tribe?.tribe ?? "";
    this.validateTribeName();
    this.tribeModal.open();
  }

  onTribeNameChange(name: string): void {
    this.tribeName = name ?? "";
    this.validateTribeName();
  }

  private validateTribeName(): void {
    const name = this.tribeName.trim();
    const duplicate = this.tribes.some(
      (t) => t.id !== this.editingTribe?.id && t.tribe.trim().toLowerCase() === name.toLowerCase(),
    );
    this.tribeNameError = duplicate ? `Já existe a tribe "${name}".` : "";
    const unchanged = !!this.editingTribe && name === this.editingTribe.tribe;
    this.tribePrimaryAction = { ...this.tribePrimaryAction, disabled: !name || duplicate || unchanged };
  }

  saveTribe(): void {
    const tribe = this.tribeName.trim();
    const request: Observable<Tribe> = this.editingTribe
      ? this.tribeService.update(this.editingTribe.id, { tribe })
      : this.tribeService.create({ tribe });

    this.tribePrimaryAction = { ...this.tribePrimaryAction, loading: true };
    this.subscriptions.add(
      request.subscribe({
        next: (saved) => {
          this.poNotification.success(this.editingTribe ? `Tribe renomeada para "${tribe}".` : `Tribe "${tribe}" criada.`);
          this.tribeModal.close();
          this.load();
          // Recém-criada: já oferece o vínculo com as áreas.
          if (!this.editingTribe) {
            this.openLinkAreas({ ...saved, areasCount: 0, usersCount: 0 });
          }
        },
        complete: () => (this.tribePrimaryAction = { ...this.tribePrimaryAction, loading: false }),
        error: () => (this.tribePrimaryAction = { ...this.tribePrimaryAction, loading: false }),
      }),
    );
  }

  confirmRemove(tribe: Tribe): void {
    if (tribe.areasCount) {
      this.poDialog.alert({
        title: "Não dá para excluir ainda",
        message: `A tribe <strong>${tribe.tribe}</strong> tem ${tribe.areasCount} área(s). Use <strong>Vincular áreas</strong> para levá-las a outra tribe antes de excluir.`,
      });
      return;
    }
    this.poDialog.confirm({
      title: "Excluir tribe",
      message: `Excluir a tribe <strong>${tribe.tribe}</strong>?`,
      confirm: () =>
        this.subscriptions.add(
          this.tribeService.remove(tribe.id).subscribe(() => {
            this.poNotification.success(`Tribe "${tribe.tribe}" excluída.`);
            this.load();
          }),
        ),
    });
  }

  // ── Vincular áreas ──────────────────────────────────────────────────────────

  openLinkAreas(tribe: Tribe): void {
    this.linkingTribe = tribe;
    const tribeName = new Map(this.tribes.map((t) => [t.id, t.tribe]));
    this.linkRows = this.areas
      .slice()
      .sort((a, b) => a.area.localeCompare(b.area))
      .map((a) => ({
        id: a.id,
        area: a.area,
        currentTribe: (a.tribe && tribeName.get(a.tribe)) || "Sem tribe",
        $selected: a.tribe === tribe.id,
      }));
    this.linkedAreaIds = this.linkRows.filter((r) => r.$selected).map((r) => r.id);
    this.linkSearch = "";
    this.filterLinkRows();
    this.refreshLinkSummary();
    this.linkAreasModal.open();
  }

  onLinkSearch(q: string): void {
    this.linkSearch = q ?? "";
    this.filterLinkRows();
  }

  private filterLinkRows(): void {
    const q = this.linkSearch.trim().toLowerCase();
    this.linkVisibleRows = q
      ? this.linkRows.filter((r) => r.area.toLowerCase().includes(q) || r.currentTribe.toLowerCase().includes(q))
      : this.linkRows;
  }

  onLinkRowToggle(row: { id: string }, selected: boolean): void {
    const ids = new Set(this.linkedAreaIds);
    if (selected) ids.add(row.id);
    else ids.delete(row.id);
    this.onLinkedAreasChange([...ids]);
  }

  onLinkAllToggle(selected: boolean): void {
    const ids = new Set(this.linkedAreaIds);
    this.linkVisibleRows.forEach((r) => (selected ? ids.add(r.id) : ids.delete(r.id)));
    this.onLinkedAreasChange([...ids]);
  }

  onLinkedAreasChange(ids: Array<string>): void {
    this.linkedAreaIds = ids ?? [];
    const selected = new Set(this.linkedAreaIds);
    this.linkRows.forEach((r) => (r.$selected = selected.has(r.id)));
    this.refreshLinkSummary();
  }

  private refreshLinkSummary(): void {
    const tribeId = this.linkingTribe?.id;
    const selected = new Set(this.linkedAreaIds);
    this.linkSummary = {
      entering: this.areas.filter((a) => selected.has(a.id) && a.tribe !== tribeId),
      leaving: this.areas.filter((a) => !selected.has(a.id) && a.tribe === tribeId),
    };
    const changes = this.linkSummary.entering.length + this.linkSummary.leaving.length;
    this.linkPrimaryAction = { ...this.linkPrimaryAction, disabled: !changes };
  }

  get movedFromOtherTribes(): number {
    return this.linkSummary.entering.filter((a) => !!a.tribe).length;
  }

  saveLinks(): void {
    const tribe = this.linkingTribe;
    if (!tribe) {
      return;
    }
    const updates = [
      ...this.linkSummary.entering.map((a) => this.areaService.update(a.id, { tribe: tribe.id })),
      ...this.linkSummary.leaving.map((a) => this.areaService.update(a.id, { tribe: null })),
    ];
    this.linkPrimaryAction = { ...this.linkPrimaryAction, loading: true };
    this.subscriptions.add(
      (updates.length ? forkJoin(updates) : of([])).subscribe({
        next: () => {
          this.poNotification.success(`Vínculos da tribe "${tribe.tribe}" atualizados.`);
          this.linkAreasModal.close();
          this.load();
        },
        complete: () => (this.linkPrimaryAction = { ...this.linkPrimaryAction, loading: false }),
        error: () => {
          this.linkPrimaryAction = { ...this.linkPrimaryAction, loading: false };
          this.load();
        },
      }),
    );
  }
}
